"use strict";

(() => {
  const toArray = (value) => Array.isArray(value) ? value : [];

  Alpine.data("quiz", () => ({
    step: 1,
    steps: 1,
    answers: [],
    loadLine: 0,
    loadLineStep: 0,
    errorContainer: null,
    variants: {},
    filteredVariants: [],
    swiper: null,
    message: "",
    desktopCarouselCanPrev: false,
    desktopCarouselCanNext: false,
    questionCount: 1,
    selectOnlyOneLabel: "Select only one",
    selectUpToLabel: "Please only select up to __COUNT__.",
    selectAtLeastOneLabel: "Please select at least 1.",
    buyLabel: "BUY",
    addedLabel: "ADDED",
    rankLabels: [],
    quizRoot: null,

    init() {
      this.quizRoot = this.$el;
      const root = this.rootElement();

      this.questionCount = Number(root.dataset.quizLength) || 1;
      this.steps = 1;
      this.loadLineStep = 100;
      this.loadLine = 100;
      this.errorContainer = this.$refs.errorContainer || root.querySelector('[x-ref="errorContainer"]');
      this.selectOnlyOneLabel = root.dataset.selectOnlyOneLabel || this.selectOnlyOneLabel;
      this.selectUpToLabel = root.dataset.selectUpToLabel || this.selectUpToLabel;
      this.selectAtLeastOneLabel = root.dataset.selectAtLeastOneLabel || this.selectAtLeastOneLabel;
      this.buyLabel = root.dataset.buyLabel || this.buyLabel;
      this.addedLabel = root.dataset.addedLabel || this.addedLabel;
      this.rankLabels = [
        root.dataset.rankFirst || "1st choice",
        root.dataset.rankSecond || "2nd choice",
        root.dataset.rankThird || "3rd choice",
        root.dataset.rankFourth || "4th choice",
        root.dataset.rankFifth || "5th choice",
        root.dataset.rankSixth || "6th choice"
      ];
      this.initAnswers();

      const variantsScript = root.querySelector("script[data-quiz-variants]");
      this.variants = JSON.parse((variantsScript && variantsScript.textContent) || "{}");

      this.$nextTick(() => {
        this.swiperStarter();
      });
    },

    rootElement() {
      return this.quizRoot || document.querySelector(".quiz--container");
    },

    rankLabel(rank) {
      return this.rankLabels[rank - 1] || `#${rank} choice`;
    },

    variantScore(variant, throughStep) {
      let total = 0;
      let strongestStep = 0;
      const matches = variant.matches_count || {};

      for (let index = 1; index <= throughStep; index += 1) {
        const stepScore = Number(matches[index] && matches[index].matches_count) || 0;
        total += stepScore;
        strongestStep = Math.max(strongestStep, stepScore);
      }

      return { total, strongestStep };
    },

    rankVariants(throughStep) {
      const ranked = Object.values(this.variants)
        .filter((variant) => variant.available === "true")
        .map((variant) => {
          const score = this.variantScore(variant, throughStep);
          return {
            ...variant,
            quizScore: score.total,
            strongestStepScore: score.strongestStep
          };
        })
        .sort((a, b) => {
          if (b.quizScore !== a.quizScore) return b.quizScore - a.quizScore;
          if (b.strongestStepScore !== a.strongestStepScore) return b.strongestStepScore - a.strongestStepScore;
          return String(a.product_title || "").localeCompare(String(b.product_title || ""));
        })
        .slice(0, 6);

      let currentRank = 0;
      let previousScore = null;

      this.filteredVariants = ranked.map((variant) => {
        if (previousScore === null || variant.quizScore !== previousScore) {
          currentRank += 1;
          previousScore = variant.quizScore;
        }

        return {
          ...variant,
          rank: currentRank,
          rankLabel: this.rankLabel(currentRank)
        };
      });

      this.updateSlider();
    },

    findMatches(stepNumber, selectedAnswers, coefficient, shouldRank = true) {
      const selectedIds = toArray(selectedAnswers).map(String);
      const weight = Number(coefficient) || 1;

      Object.entries(this.variants).forEach(([id, variant]) => {
        const recommendationOptions = toArray(variant.recommendation_options).map(String);
        const matchesCount = selectedIds.reduce((total, answerId) => {
          const matchCount = recommendationOptions.filter((optionId) => optionId === answerId).length;
          return total + (matchCount * weight);
        }, 0);

        this.variants[id].matches_count = this.variants[id].matches_count || {};
        this.variants[id].matches_count[stepNumber] = { matches_count: matchesCount };
      });

      if (shouldRank) this.rankVariants(stepNumber);
    },

    swiperStarter() {
      const root = this.rootElement();
      const swiperElement = root ? root.querySelector(".swiper") : null;
      if (!swiperElement || typeof Swiper === "undefined") return;

      this.swiper = new Swiper(swiperElement, {
        autoHeight: true,
        enabled: true,
        grabCursor: true,
        pagination: {
          clickable: true,
          el: swiperElement.querySelector(".swiper-pagination")
        },
        slidesPerView: 1,
        spaceBetween: 18,
        watchOverflow: true,
        breakpoints: {
          750: {
            spaceBetween: 26
          },
          990: {
            slidesPerView: 3.2,
            spaceBetween: 18
          }
        },
        on: {
          afterInit: (swiper) => this.updateResultNavigation(swiper),
          breakpoint: (swiper) => this.updateResultNavigation(swiper),
          resize: (swiper) => this.updateResultNavigation(swiper),
          slideChange: (swiper) => this.updateResultNavigation(swiper),
          transitionEnd: (swiper) => this.updateResultNavigation(swiper),
          update: (swiper) => this.updateResultNavigation(swiper)
        }
      });
      this.updateResultNavigation(this.swiper);
    },

    updateSlider() {
      window.requestAnimationFrame(() => {
        if (!this.swiper) this.swiperStarter();
        if (!this.swiper) return;
        this.swiper.update();
        this.swiper.slideTo(0, 0);
        this.swiper.updateAutoHeight(0);
        this.updateResultNavigation();
      });
    },

    updateResultNavigation(swiperInstance = null) {
      const swiper = swiperInstance || this.swiper;
      const isDesktop = window.matchMedia("(min-width: 990px)").matches;
      const canScroll = !!swiper && !swiper.isLocked && toArray(this.filteredVariants).length > 1;

      this.desktopCarouselCanPrev = isDesktop && canScroll && !swiper.isBeginning;
      this.desktopCarouselCanNext = isDesktop && canScroll && !swiper.isEnd;
    },

    slideResults(direction) {
      if (!this.swiper) return;

      if (direction === "prev") {
        this.swiper.slidePrev();
      } else {
        this.swiper.slideNext();
      }

      window.setTimeout(() => {
        this.updateResultNavigation();
      }, 0);
    },

    scrollToQuiz() {
      const rootElement = this.rootElement();
      if (!rootElement) return;

      const top = rootElement.offsetTop || (rootElement.getBoundingClientRect().top + window.scrollY);
      const root = document.documentElement;
      const scroller = document.scrollingElement || root;
      const scrollBehavior = root.style.scrollBehavior;

      root.style.scrollBehavior = "auto";
      scroller.scrollTop = top;
      window.scrollTo(0, top);
      root.style.scrollBehavior = scrollBehavior;
    },

    settleQuizScroll() {
      this.scrollToQuiz();
      window.requestAnimationFrame(() => {
        this.scrollToQuiz();
      });
      [120, 420, 900, 1400].forEach((delay) => {
        window.setTimeout(() => {
          this.scrollToQuiz();
        }, delay);
      });
    },

    settleResultScroll() {
      this.settleQuizScroll();
      window.setTimeout(() => {
        this.updateSlider();
        this.scrollToQuiz();
      }, 700);
    },

    showError(message) {
      const error = document.createElement("div");
      error.classList.add("quiz__prompt-error");
      if (message) error.innerHTML = message;
      if (this.errorContainer) this.errorContainer.appendChild(error);

      window.setTimeout(() => {
        error.style.opacity = "1";
      }, 10);

      window.setTimeout(() => {
        error.style.opacity = "0";
        window.setTimeout(() => {
          error.remove();
        }, 300);
      }, 3000);
    },

    initAnswers() {
      this.answers = Array.from({ length: this.questionCount }, () => []);
    },

    selectOption(event) {
      const button = event && event.currentTarget ? event.currentTarget : this.rootElement();
      const maxAnswers = button.dataset.maxAnswers;
      const optionId = String(button.dataset.id || "");
      const optionStep = Number(button.dataset.step) || this.step;
      const stepIndex = optionStep - 1;
      const root = this.rootElement();
      this.answers[stepIndex] = this.answers[stepIndex] || [];

      if (button.classList.contains("active")) {
        button.classList.remove("active");
        this.answers[stepIndex] = this.answers[stepIndex].filter((answerId) => answerId !== optionId);
        this.message = "";
        return;
      }

      if (maxAnswers === "1") {
        if (root) root.querySelectorAll(`[option-step-${optionStep}].active`).forEach((activeButton) => {
          activeButton.classList.remove("active");
        });
        this.answers[stepIndex] = [];
      } else if (maxAnswers && this.answers[stepIndex].length >= Number(maxAnswers)) {
        const message = maxAnswers === "1"
          ? this.selectOnlyOneLabel
          : this.selectUpToLabel.replace("__COUNT__", maxAnswers);

        this.showError(message);
        button.classList.add("quiz__select--error");
        window.setTimeout(() => {
          button.classList.remove("quiz__select--error");
        }, 500);
        return;
      }

      this.message = button.dataset.message || "";
      button.classList.add("active");
      this.answers[stepIndex] = [...new Set([...this.answers[stepIndex], optionId])];
    },

    next() {
      const missingStep = this.answers.findIndex((answers, index) => index < this.questionCount && !answers.length);

      if (missingStep !== -1) {
        this.showError(this.selectAtLeastOneLabel);
        return;
      }

      const root = this.rootElement();
      Object.values(this.variants).forEach((variant) => {
        delete variant.matches_count;
      });

      for (let index = 1; index <= this.questionCount; index += 1) {
        const activeOption = root ? root.querySelector(`[option-step-${index}].active`) : null;
        const coefficient = activeOption ? activeOption.dataset.priorityCoefficient : 1;
        this.findMatches(index, this.answers[index - 1] || [], coefficient, false);
      }

      this.rankVariants(this.questionCount);
      this.step = this.steps + 1;
      this.loadLine = 100;
      this.message = "";
      this.settleResultScroll();

      this.$nextTick(() => {
        this.settleResultScroll();
        this.updateSlider();
      });
    },

    prev() {
      const currentStep = this.step;

      this.answers[currentStep - 1] = [];
      const root = this.rootElement();
      if (root) root.querySelectorAll(`[option-step-${currentStep}]`).forEach((button) => {
        button.classList.remove("active");
      });

      Object.values(this.variants).forEach((variant) => {
        if (variant.matches_count) delete variant.matches_count[currentStep];
      });

      this.step -= 1;
      this.loadLine -= this.loadLineStep;
      this.message = "";

      this.$nextTick(() => {
        this.settleQuizScroll();
      });
    },

    restartSlider() {
      if (this.swiper) {
        this.swiper.slideTo(0, 0);
        this.swiper.updateAutoHeight(0);
      }
    },

    restartQuiz() {
      this.step = 1;
      this.loadLine = this.loadLineStep;
      this.message = "";
      this.filteredVariants = [];
      this.desktopCarouselCanPrev = false;
      this.desktopCarouselCanNext = false;
      const root = this.rootElement();
      if (root) root.querySelectorAll(".quiz__select.active").forEach((button) => {
        button.classList.remove("active");
      });
      Object.values(this.variants).forEach((variant) => {
        delete variant.matches_count;
      });
      this.restartSlider();
      this.initAnswers();
      this.$nextTick(() => {
        this.settleQuizScroll();
      });
    },

    addToCart(variant, event) {
      const button = event && event.currentTarget ? event.currentTarget : this.rootElement();
      if (button.ariaDisabled === "true") return;

      const groupId = utils.generateUUID();
      const productUrl = variant.url;
      const upgradeSku = variant.sku.replace(/-T$/, "-TP");
      const items = [
        {
          id: variant.id,
          quantity: 1,
          properties: {
            _groupId: groupId,
            _url: productUrl,
            _page_of_purchase: window.location.pathname,
            _isAddon: false,
            _isUpgradable: true,
            _currentAvailableUpgradeVariants: upgradeSku !== variant.sku ? upgradeSku : "",
            _currentAvailableUpgradeSKU: upgradeSku !== variant.sku ? upgradeSku : ""
          }
        }
      ];

      try {
        button.setAttribute("aria-disabled", "true");
        button.classList.add("loading");
        const overlay = button.querySelector(".loading-overlay");
        const label = button.querySelector(".buy-button-text");
        if (overlay) overlay.classList.remove("hidden");
        if (label) label.classList.add("hidden");

        fetch(window.routes.cart_add_url, {
          body: JSON.stringify({ items }),
          headers: { "Content-Type": "application/json" },
          method: "POST"
        }).then(() => {
          window.open(window.routes.cart_url, "_self");
        }).catch((error) => {
          console.error({ error });
        }).finally(() => {
          button.setAttribute("aria-disabled", "false");
          button.classList.remove("loading");
          if (overlay) overlay.classList.add("hidden");
          if (label) label.classList.remove("hidden");
        });
      } catch (error) {
        console.error({ error });
      }
    }
  }));

  function initQuizDock() {
    const dock = document.querySelector("[data-quiz-v2-dock]");
    if (!dock || dock.dataset.quizDockReady === "true") return;

    dock.dataset.quizDockReady = "true";
    const links = Array.from(dock.querySelectorAll('a[href^="#"]'));
    const items = links.map((link) => {
      const selector = link.getAttribute("data-quiz-v2-spy") || link.getAttribute("href");
      return {
        link,
        target: selector ? document.querySelector(selector) : null
      };
    }).filter((item) => item.target);

    const setActive = (activeLink) => {
      links.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle("is-active", isActive);
        if (isActive) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    };

    const updateDock = () => {
      if (!items.length) return;
      const triggerLine = window.innerHeight * 0.42;
      let activeItem = items[0];

      items.forEach((item) => {
        if (!item.target.getClientRects().length) return;
        if (item.target.getBoundingClientRect().top <= triggerLine) {
          activeItem = item;
        }
      });

      setActive(activeItem.link);
    };

    links.forEach((link) => {
      link.addEventListener("click", () => {
        setActive(link);
        window.setTimeout(updateDock, 360);
      });
    });

    updateDock();
    window.addEventListener("scroll", updateDock, { passive: true });
    window.addEventListener("resize", updateDock);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuizDock);
  } else {
    initQuizDock();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target && event.target.querySelector("[data-quiz-v2-dock]")) {
      initQuizDock();
    }
  });
})();
